import { trackEvent } from "@/lib/gtm";
import { trackMetaEvent } from "@/lib/meta-pixel";

/**
 * Ad platform conversions. Each helper pushes a GTM `dataLayer` event (for
 * Google Ads / GA4 triggers built in the containers) and the matching Meta
 * Pixel standard event. Call these beside the PostHog event for the same
 * action, never instead of it: PostHog is the product analytics source of
 * truth. Nothing here sends PII (names, emails, phone numbers).
 *
 * | Action                         | dataLayer              | Meta                     |
 * | ------------------------------ | ---------------------- | ------------------------ |
 * | Marketing form submitted       | `form_submitted`       | `Lead` (not for `demo`)  |
 * | Demo booked on cal.com         | `demo_booked`          | `Lead` + `Schedule`      |
 * | Account created at /signup     | `sign_up`              | `CompleteRegistration`   |
 * | Subscription started (onboard) | `subscription_created` | `StartTrial`/`Subscribe` |
 */

/**
 * The demo "form" is the brand-type pick that opens the scheduler; its Meta
 * lead is the booking itself (`trackDemoBookedConversion`).
 */
export function trackFormSubmittedConversion(form: string, props?: { brandType?: string }) {
  trackEvent("form_submitted", { form, ...props });
  if (form !== "demo") {
    trackMetaEvent("Lead", { content_name: form });
  }
}

export function trackDemoBookedConversion(brandType: string) {
  trackEvent("demo_booked", { brandType });
  trackMetaEvent("Lead", { content_name: "demo", content_category: brandType });
  trackMetaEvent("Schedule", { content_name: "demo", content_category: brandType });
}

export function trackSignUpConversion() {
  trackEvent("sign_up", { method: "email" });
  trackMetaEvent("CompleteRegistration", { content_name: "signup", status: true });
}

export function trackSubscriptionConversion(props: {
  plan: string;
  billingInterval: "month" | "year";
  freeTrial: boolean;
  /** Price of one billing interval in USD (a year for annual plans). */
  value: number;
}) {
  const currency = "USD";
  trackEvent("subscription_created", { ...props, currency });
  trackMetaEvent(props.freeTrial ? "StartTrial" : "Subscribe", {
    value: props.value,
    currency,
    content_name: props.plan,
  });
}
